import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ThemeMaker from "../../components/thememaker";
import { Paginated } from "../../components/Paginated";
import "../../components/table.css"
import { config } from "../../utils/config";
import CircularProgress from '@mui/material/CircularProgress';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./Rider.module.css";
import "../../components/mcrbTableTheme.css";
import "../../index.css"

const { baseNodeapiUrl } = config;

const toastOpts: any = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const STATUS_OPTIONS = ["Created", "Accepted", "In-progress", "Completed", "Cancelled"];

function statusPillClass(rawStatus: any) {
    const s = (rawStatus || "").toString().toLowerCase();
    if (s.includes("completed")) return styles.pillGreen;
    if (s.includes("cancelled") || s.includes("canceled")) return styles.pillRed;
    if (s.includes("progress")) return styles.pillBlue;
    if (s.includes("accepted")) return styles.pillAmber;
    return styles.pillGrey;
}

function isRiderAssigned(riderName: any) {
    return riderName && !/no rider assigned/i.test(riderName);
}

const AllRiderDeliveries = () => {
    const [deliveries, setDeliveries] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const fetchDeliveries = async () => {
        setLoading(true);
        try {
            const { data } = await axios({
                url: `${baseNodeapiUrl}/invlrider/super-admin/rider-orders`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                },
                params: { page: 1, limit: 1000 }
            });
            if (data.success) {
                setDeliveries(data.data || []);
            } else {
                toast.error(data.message || "Failed to fetch rider deliveries !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong while fetching rider deliveries !", toastOpts);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchDeliveries();
    }, []);

    const filteredDeliveries = useMemo(() => {
        const query = search.trim().toLowerCase();
        return deliveries.filter((item: any) => {
            if (statusFilter && item.status !== statusFilter) return false;
            if (!query) return true;
            return [item.order_id, item.customer_name, item.seller_name, item.rider_name]
                .some((field: any) => (field || "").toString().toLowerCase().includes(query));
        });
    }, [deliveries, search, statusFilter]);

    const COLUMNS = [
        {
            Header: "Sn.",
            Footer: "Sn.",
            accessor: (row: any, count: any) => <>{count + 1}</>,
            disableFilters: true,
            sticky: "left",
        },
        {
            Header: "Order ID",
            Footer: "Order ID",
            accessor: (row: any) => <span style={{ color: "#1F2430", fontWeight: 700, fontFamily: "'Sora', 'Inter', sans-serif" }}>{row.order_id}</span>,
            sticky: "left",
        },
        {
            Header: "Seller",
            Footer: "Seller",
            accessor: (row: any) => (
                <div>
                    <div>{row.seller_name || "-"}</div>
                    {row.seller_contact ? <span className={styles.nameSub}>{row.seller_contact}</span> : null}
                </div>
            ),
            sticky: "left",
        },
        {
            Header: "Rider",
            Footer: "Rider",
            accessor: (row: any) => (
                isRiderAssigned(row.rider_name) ? (
                    <div>
                        <div>{row.rider_name}</div>
                        {row.rider_contact ? <span className={styles.nameSub}>{row.rider_contact}</span> : null}
                    </div>
                ) : <span className={styles.noRider}>Unassigned</span>
            ),
            sticky: "left",
        },
        {
            Header: "Customer",
            Footer: "Customer",
            accessor: (row: any) => (
                <div>
                    <div>{row.customer_name || "-"}</div>
                    {row.customer_no ? <span className={styles.nameSub}>{row.customer_no}</span> : null}
                </div>
            ),
            sticky: "left",
        },
        {
            Header: "Pickup Address",
            Footer: "Pickup Address",
            accessor: (row: any) => <span className={styles.clampText} title={row.pickup_address}>{row.pickup_address || "-"}</span>,
            sticky: "left",
        },
        {
            Header: "Drop Address",
            Footer: "Drop Address",
            accessor: (row: any) => <span className={styles.clampText} title={row.drop_address}>{row.drop_address || "-"}</span>,
            sticky: "left",
        },
        {
            Header: "Status",
            Footer: "Status",
            accessor: (row: any) => <span className={`${styles.pillBase} ${statusPillClass(row.status)}`}>{row.status || "-"}</span>,
            sticky: "left",
        },
    ];

    return (
        <ThemeMaker>
            <div className={`${styles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>All Rider Deliveries</span>
                    </div>

                    <div className={styles.toolbarRow}>
                        <input
                            type="search"
                            className={styles.searchInput}
                            placeholder="Search by Order ID, Customer, Seller or Rider"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Status</option>
                            {STATUS_OPTIONS.map((s) => (
                                <option value={s} key={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div className={`${brandStyles.bodyArea} ${brandStyles.tableWrap}`} style={{ minHeight: "60vh" }}>
                        {
                            loading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <>
                                    <div className={styles.desktopTable}>
                                        <Paginated data={filteredDeliveries} columns={COLUMNS} />
                                    </div>

                                    <div className={styles.mobileList}>
                                        {
                                            filteredDeliveries.map((item: any, index: number) => (
                                                <div className={styles.mobileCard} key={item.order_id ?? index}>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Order ID</span>
                                                        <span className={styles.mobileValue}>{item?.order_id}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Seller</span>
                                                        <span className={styles.mobileValue}>{item?.seller_name}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Rider</span>
                                                        {isRiderAssigned(item?.rider_name) ? (
                                                            <span className={styles.mobileValue}>{item.rider_name}</span>
                                                        ) : <span className={styles.noRider}>Unassigned</span>}
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Customer</span>
                                                        <span className={styles.mobileValue}>{item?.customer_name} ({item?.customer_no})</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Pickup</span>
                                                        <span className={styles.mobileValue}>{item?.pickup_address}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Drop</span>
                                                        <span className={styles.mobileValue}>{item?.drop_address}</span>
                                                    </div>
                                                    <div className={styles.mobileRow}>
                                                        <span className={styles.mobileLabel}>Status</span>
                                                        <span className={`${styles.pillBase} ${statusPillClass(item?.status)}`}>{item?.status || "-"}</span>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>

                                    {filteredDeliveries.length === 0 ? <p className={styles.emptyState}>No rider deliveries found !</p> : null}
                                </>
                            )
                        }
                    </div>
                </div>
            </div>
        </ThemeMaker>
    )
}

export default AllRiderDeliveries;
